// src/components/TokenHistory.js
import React, { useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { db } from "../utils/firebase";
import "./TokenHistory.css";

function formatTs(ts) {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts.seconds * 1000);
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

function reasonLabel(reason) {
  switch (reason) {
    case "Train_with_AI": return "Train with AI";
    case "Talk_with_AI":  return "Talk with AI";
    case "room_entry": return "Room Entry";
    case "room_lock_3h": return "Room Lock (3h)";
    case "subscription_cycle": return "Monthly Credit";
    case "one_time_purchase": return "One-time Purchase";
    default: return reason || "Other";
  }
}

export default function TokenHistory({ pageSize = 50, title = "Token History" }) {
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (u) => setUser(u || null));
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!user) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const q = query(
      collection(db, "users", user.uid, "token_ledger"),
      orderBy("ts", "desc"),
      limit(pageSize)
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const next = [];
        snap.forEach((doc) => next.push({ id: doc.id, ...doc.data() }));
        setRows(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [user, pageSize]);

  return (
    <section className="TH-Card">
      <header className="TH-Header">
        <h3 className="TH-Title">{title}</h3>
        {loading && <span className="TH-Subtle">Loading…</span>}
      </header>

      {!loading && rows.length === 0 ? (
        <p className="TH-Empty">No token activity yet.</p>
      ) : (
        <ul className="TH-List" aria-live="polite">
          {rows.map((r) => {
            const raw = Number(r.amount ?? 0);
            const mag = Math.abs(raw);
            const hasType = typeof r.type === "string";
            const isDebit = r.type === "debit";
            // If 'type' exists, treat stored amount as magnitude and apply sign.
            // If 'type' is absent, trust the legacy signed value.
            const signed = hasType ? (isDebit ? -mag : mag) : raw;
            const amtStr = `${signed > 0 ? "+" : ""}${signed}`;
            const amountClass = signed >= 0 ? "pos" : "neg";
            const meta =
              r.metadata?.roomId ? `room: ${r.metadata.roomId}` :
              r.metadata?.invoiceId ? `invoice: ${r.metadata.invoiceId}` :
              "";

            const reason = r.reason;
            const reasonClass =
              reason === "subscription_cycle" ? "is-success" :
              reason && reason.startsWith("room_") ? "is-warn" : "is-default";

            return (
              <li key={r.id} className="TH-Row">
                <time className="TH-Time" title={String(r.ts?.seconds || "")}>
                  {formatTs(r.ts)}
                </time>
                <span className={`TH-Amount ${amountClass}`}>{amtStr}</span>
                <span className={`TH-Chip ${reasonClass}`}>{reasonLabel(reason)}</span>
                <span className="TH-Meta" title={meta || ""}>{meta || "—"}</span>
                <span className="TH-Balance">Bal: {r.balance_after ?? "—"}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
